import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import {
  Receipt, Loader2, Clock, CheckCircle2, AlertCircle,
  FileText, DollarSign, Calendar, Search, Plus, Send,
  Trash2, RefreshCw, Users, Mail, X
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const InvoicePageShell = ({ embedded, children }) => (
  embedded ? children : <AdminLayout title="Family Receivables">{children}</AdminLayout>
);

const AdminOrgInvoices = ({ embedded = false }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Create invoice
  const [createDialog, setCreateDialog] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [households, setHouseholds] = useState([]);
  const [loadingHouseholds, setLoadingHouseholds] = useState(false);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [lineItems, setLineItems] = useState([{ description: "", quantity: 1, unit_price: 0, total: 0 }]);
  const [dueDate, setDueDate] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [creating, setCreating] = useState(false);

  // Re-enrollment
  const [reEnrollDialog, setReEnrollDialog] = useState(false);
  const [reEnrollStep, setReEnrollStep] = useState("form"); // form | preview | done
  const [sourceProgramId, setSourceProgramId] = useState("");
  const [targetProgramId, setTargetProgramId] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [dueDays, setDueDays] = useState(14);
  const [reEnrollMessage, setReEnrollMessage] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executingReEnroll, setExecutingReEnroll] = useState(false);

  // Reminder
  const [sendingReminder, setSendingReminder] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => { fetchInvoices(); fetchPrograms(); }, [token]);
  useEffect(() => { setPage(1); }, [filter]);

  const fetchInvoices = async (p = 1) => {
    try {
      const params = new URLSearchParams({ page: p, page_size: 50 });
      if (filter !== "all") params.append("status", filter);
      const res = await axios.get(`${API}/org-invoices?${params}`, { headers });
      setInvoices(res.data.items || []);
      setTotalCount(res.data.total || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (!loading) fetchInvoices(page); }, [filter, page]);

  const fetchPrograms = async () => {
    try {
      const res = await axios.get(`${API}/programs`, { headers });
      setPrograms(res.data?.items || res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchHouseholds = async (programId) => {
    if (!programId) { setHouseholds([]); return; }
    setLoadingHouseholds(true);
    try {
      const res = await axios.get(`${API}/org-invoices/program-households/${programId}`, { headers });
      setHouseholds(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load households");
    } finally {
      setLoadingHouseholds(false);
    }
  };

  // Status helpers
  const getStatusColor = (status) => {
    switch (status) {
      case "paid": return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "pending": return "bg-amber-50 text-amber-700 border-amber-200";
      case "overdue": return "bg-red-50 text-red-700 border-red-200";
      case "cancelled": return "bg-slate-50 text-slate-500 border-slate-200";
      default: return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid": return <CheckCircle2 className="w-3.5 h-3.5" />;
      case "pending": return <Clock className="w-3.5 h-3.5" />;
      case "overdue": return <AlertCircle className="w-3.5 h-3.5" />;
      default: return <FileText className="w-3.5 h-3.5" />;
    }
  };

  // Computed stats
  const totalOutstanding = invoices.filter(i => i.status === "pending" || i.status === "overdue").reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0);

  // Filtered by search
  const filteredInvoices = invoices.filter(inv => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (inv.household_name || "").toLowerCase().includes(q)
      || (inv.parent_name || "").toLowerCase().includes(q)
      || (inv.parent_email || "").toLowerCase().includes(q)
      || (inv.description || "").toLowerCase().includes(q);
  });

  // Line item helpers
  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === "quantity" || field === "unit_price") {
        updated.total = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.unit_price) || 0);
        updated.total = Math.round(updated.total * 100) / 100;
      }
      return updated;
    }));
  };

  const addLineItem = () => setLineItems(prev => [...prev, { description: "", quantity: 1, unit_price: 0, total: 0 }]);
  const removeLineItem = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));
  const invoiceTotal = lineItems.reduce((s, li) => s + (li.total || 0), 0);

  // Create invoice
  const handleCreate = async () => {
    if (!selectedHousehold) { toast.error("Select a household"); return; }
    const validItems = lineItems.filter(li => li.description && li.total > 0);
    if (validItems.length === 0) { toast.error("Add at least one line item"); return; }

    setCreating(true);
    try {
      await axios.post(`${API}/org-invoices`, {
        household_id: selectedHousehold.household_id,
        user_id: selectedHousehold.user_id,
        program_id: selectedProgramId || undefined,
        player_ids: selectedPlayerIds.length > 0 ? selectedPlayerIds : undefined,
        description: validItems.map(i => i.description).join(", "),
        line_items: validItems,
        due_date: dueDate || undefined,
        notes: invoiceNotes || undefined,
      }, { headers });
      toast.success("Invoice created and sent!");
      setCreateDialog(false);
      resetCreateForm();
      fetchInvoices(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setSelectedProgramId("");
    setHouseholds([]);
    setSelectedHousehold(null);
    setSelectedPlayerIds([]);
    setLineItems([{ description: "", quantity: 1, unit_price: 0, total: 0 }]);
    setDueDate("");
    setInvoiceNotes("");
  };

  // Reminder
  const handleRemind = async (invoiceId) => {
    setSendingReminder(invoiceId);
    try {
      await axios.post(`${API}/org-invoices/${invoiceId}/remind`, {}, { headers });
      toast.success("Reminder sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  // Re-enrollment preview
  const handlePreview = async () => {
    if (!sourceProgramId || !targetProgramId) { toast.error("Select both programs"); return; }
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/org-invoices/bulk-re-enroll/preview`, {
        source_program_id: sourceProgramId,
        target_program_id: targetProgramId,
        custom_amount: customAmount ? parseFloat(customAmount) : undefined,
        due_days: dueDays,
        message: reEnrollMessage || undefined,
      }, { headers });
      setPreviewData(res.data);
      setReEnrollStep("preview");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExecuteReEnroll = async () => {
    setExecutingReEnroll(true);
    try {
      const res = await axios.post(`${API}/org-invoices/bulk-re-enroll`, {
        source_program_id: sourceProgramId,
        target_program_id: targetProgramId,
        custom_amount: customAmount ? parseFloat(customAmount) : undefined,
        due_days: dueDays,
        message: reEnrollMessage || undefined,
      }, { headers });
      toast.success(`${res.data.invoices_created} re-enrollment invoices sent!`);
      setReEnrollDialog(false);
      resetReEnrollForm();
      fetchInvoices(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-enrollment failed");
    } finally {
      setExecutingReEnroll(false);
    }
  };

  const resetReEnrollForm = () => {
    setSourceProgramId("");
    setTargetProgramId("");
    setCustomAmount("");
    setDueDays(14);
    setReEnrollMessage("");
    setPreviewData(null);
    setReEnrollStep("form");
  };

  if (loading) {
    return (
      <InvoicePageShell embedded={embedded}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </InvoicePageShell>
    );
  }

  return (
    <InvoicePageShell embedded={embedded}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Family Receivables</h2>
            <p className="text-slate-500 mt-1">Create and manage invoices sent to families and households.</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => { resetReEnrollForm(); setReEnrollDialog(true); }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Re-Enrollment
            </Button>
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
              onClick={() => { resetCreateForm(); setCreateDialog(true); }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${totalOutstanding.toFixed(2)}</p>
                <p className="text-sm text-slate-500">Outstanding</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${totalPaid.toFixed(2)}</p>
                <p className="text-sm text-slate-500">Total Paid</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Receipt className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalCount}</p>
                <p className="text-sm text-slate-500">Total Invoices</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs + Search */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex gap-2 bg-slate-100 p-1 rounded-xl w-fit">
            {[
              { value: "all", label: "All" },
              { value: "pending", label: "Pending" },
              { value: "paid", label: "Paid" },
              { value: "overdue", label: "Overdue" },
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => setFilter(tab.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === tab.value
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search household or parent..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 w-64"
            />
          </div>
        </div>

        {/* Invoice Table */}
        {filteredInvoices.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">No Invoices</h3>
            <p className="text-slate-500">
              {filter === "all" ? "No invoices created yet" : `No ${filter} invoices found`}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Household</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Parent Email</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Description</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">Amount</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Due Date</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredInvoices.map(inv => (
                    <tr key={inv.invoice_id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{inv.household_name || "—"}</p>
                        {inv.parent_name && <p className="text-xs text-slate-400">{inv.parent_name}</p>}
                      </td>
                      <td className="py-3 px-4 text-slate-600">{inv.parent_email || "—"}</td>
                      <td className="py-3 px-4 text-slate-600 max-w-[200px] truncate">{inv.description}</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-900">${(inv.total || 0).toFixed(2)}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(inv.status)}`}>
                          {getStatusIcon(inv.status)}
                          {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-500">
                        {inv.due_date ? (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(inv.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {inv.status === "pending" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-lg text-xs"
                            disabled={sendingReminder === inv.invoice_id}
                            onClick={() => handleRemind(inv.invoice_id)}
                          >
                            {sendingReminder === inv.invoice_id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-3.5 h-3.5 mr-1" />
                                Remind
                              </>
                            )}
                          </Button>
                        )}
                        {inv.status === "overdue" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-lg text-xs border-red-200 text-red-600 hover:bg-red-50"
                            disabled={sendingReminder === inv.invoice_id}
                            onClick={() => handleRemind(inv.invoice_id)}
                          >
                            {sendingReminder === inv.invoice_id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <>
                                <AlertCircle className="w-3.5 h-3.5 mr-1" />
                                Remind
                              </>
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ============ Create Invoice Dialog ============ */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>Send an invoice to a parent/household for payment.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Program selector */}
            <div>
              <label className="text-sm font-medium text-slate-700">Program</label>
              <select
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
                value={selectedProgramId}
                onChange={e => {
                  setSelectedProgramId(e.target.value);
                  setSelectedHousehold(null);
                  setSelectedPlayerIds([]);
                  fetchHouseholds(e.target.value);
                }}
              >
                <option value="">Select a program...</option>
                {programs.map(p => (
                  <option key={p.program_id} value={p.program_id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Household selector */}
            <div>
              <label className="text-sm font-medium text-slate-700">Household *</label>
              {loadingHouseholds ? (
                <div className="flex items-center gap-2 mt-1 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading households...
                </div>
              ) : (
                <select
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
                  value={selectedHousehold?.household_id || ""}
                  onChange={e => {
                    const hh = households.find(h => h.household_id === e.target.value);
                    setSelectedHousehold(hh || null);
                    setSelectedPlayerIds([]);
                  }}
                >
                  <option value="">{selectedProgramId ? "Select a household..." : "Select a program first"}</option>
                  {households.map(hh => (
                    <option key={hh.household_id} value={hh.household_id}>
                      {hh.parent_name} — {hh.parent_email || "no email"}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Player checkboxes */}
            {selectedHousehold && selectedHousehold.player_ids?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-slate-700">Players</label>
                <div className="mt-1 space-y-2">
                  {selectedHousehold.player_ids.map((pid, idx) => (
                    <label key={pid} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedPlayerIds.includes(pid)}
                        onChange={e => {
                          setSelectedPlayerIds(prev =>
                            e.target.checked ? [...prev, pid] : prev.filter(id => id !== pid)
                          );
                        }}
                        className="rounded border-slate-300"
                      />
                      {selectedHousehold.player_names?.[idx] || pid}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Line Items *</label>
                <button onClick={addLineItem} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" /> Add Row
                </button>
              </div>
              <div className="space-y-2">
                {lineItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={e => updateLineItem(idx, "description", e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                    <input
                      type="number"
                      min="1"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={e => updateLineItem(idx, "quantity", parseInt(e.target.value) || 1)}
                      className="w-16 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 text-center"
                    />
                    <div className="relative w-28">
                      <DollarSign className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Price"
                        value={item.unit_price || ""}
                        onChange={e => updateLineItem(idx, "unit_price", parseFloat(e.target.value) || 0)}
                        className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      />
                    </div>
                    <span className="w-20 text-right text-sm font-medium text-slate-900">${item.total.toFixed(2)}</span>
                    {lineItems.length > 1 && (
                      <button onClick={() => removeLineItem(idx)} className="text-slate-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between text-sm font-semibold">
                <span className="text-slate-700">Total</span>
                <span className="text-slate-900">${invoiceTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Due date */}
            <div>
              <label className="text-sm font-medium text-slate-700">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-700">Notes</label>
              <textarea
                rows={2}
                value={invoiceNotes}
                onChange={e => setInvoiceNotes(e.target.value)}
                placeholder="Optional notes..."
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
              disabled={creating}
              onClick={handleCreate}
            >
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><Send className="w-4 h-4 mr-2" />Create &amp; Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Re-Enrollment Dialog ============ */}
      <Dialog open={reEnrollDialog} onOpenChange={(open) => { if (!open) { setReEnrollDialog(false); resetReEnrollForm(); } else setReEnrollDialog(true); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Re-Enrollment</DialogTitle>
            <DialogDescription>
              {reEnrollStep === "form" && "Send invoices to returning households from a previous program."}
              {reEnrollStep === "preview" && "Review which households will receive re-enrollment invoices."}
            </DialogDescription>
          </DialogHeader>

          {reEnrollStep === "form" && (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Source Program *</label>
                <select
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  value={sourceProgramId}
                  onChange={e => setSourceProgramId(e.target.value)}
                >
                  <option value="">Select source program...</option>
                  {programs.map(p => <option key={p.program_id} value={p.program_id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Target Program *</label>
                <select
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  value={targetProgramId}
                  onChange={e => setTargetProgramId(e.target.value)}
                >
                  <option value="">Select target program...</option>
                  {programs.map(p => <option key={p.program_id} value={p.program_id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Custom Amount Override</label>
                <div className="relative mt-1">
                  <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Leave blank to use target program price"
                    value={customAmount}
                    onChange={e => setCustomAmount(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Due in (days)</label>
                <input
                  type="number"
                  min="1"
                  value={dueDays}
                  onChange={e => setDueDays(parseInt(e.target.value) || 14)}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Custom Message</label>
                <textarea
                  rows={3}
                  placeholder="Optional message included in the invoice email..."
                  value={reEnrollMessage}
                  onChange={e => setReEnrollMessage(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>
            </div>
          )}

          {reEnrollStep === "preview" && previewData && (
            <div className="space-y-4 py-2">
              {/* Summary badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-emerald-700">{previewData.eligible}</p>
                  <p className="text-xs text-emerald-600">Eligible</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-amber-700">{previewData.already_invoiced}</p>
                  <p className="text-xs text-amber-600">Already Invoiced</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-blue-700">{previewData.already_registered}</p>
                  <p className="text-xs text-blue-600">Already Registered</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-slate-700">{previewData.no_email}</p>
                  <p className="text-xs text-slate-500">No Email</p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-600">
                <p><strong>{previewData.source_program}</strong> → <strong>{previewData.target_program}</strong></p>
                <p className="mt-1">Price per household: <strong>${previewData.target_price?.toFixed(2)}</strong></p>
              </div>

              {/* Household list */}
              <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                {previewData.households?.map(hh => (
                  <div key={hh.household_id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <div>
                      <p className="font-medium text-slate-900">{hh.parent_name}</p>
                      <p className="text-xs text-slate-400">{hh.parent_email || "No email"} · {hh.player_count} player(s)</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                      hh.status === "eligible" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : hh.status === "already_invoiced" ? "bg-amber-50 text-amber-700 border-amber-200"
                        : hh.status === "already_registered" ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                    }`}>
                      {hh.status.replace(/_/g, " ")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            {reEnrollStep === "form" && (
              <>
                <Button variant="outline" className="rounded-xl" onClick={() => setReEnrollDialog(false)}>Cancel</Button>
                <Button
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
                  disabled={loadingPreview}
                  onClick={handlePreview}
                >
                  {loadingPreview ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</> : <><Users className="w-4 h-4 mr-2" />Preview</>}
                </Button>
              </>
            )}
            {reEnrollStep === "preview" && (
              <>
                <Button variant="outline" className="rounded-xl" onClick={() => setReEnrollStep("form")}>Back</Button>
                <Button
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
                  disabled={executingReEnroll || !previewData?.eligible}
                  onClick={handleExecuteReEnroll}
                >
                  {executingReEnroll ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Mail className="w-4 h-4 mr-2" />Send {previewData?.eligible} Invoices</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </InvoicePageShell>
  );
};

export default AdminOrgInvoices;
